/**
 * Connection runner for the global SSE (Server-Sent Events) base layer.
 *
 * This is the central unit of the streaming layer: it owns the `fetch`-based
 * connection lifecycle, bearer-header auth + one-shot token refresh, reconnect
 * with full-jitter exponential backoff, `Last-Event-ID` resume, stale/connect
 * timeouts, pausing while the tab is hidden, and idempotent teardown.
 *
 * It builds on three pure units (do not duplicate their logic here):
 * - `SSEParser` — bytes -> SSE frames.
 * - `classifyResponse` / `classifyError` — HTTP/error -> next action.
 * - the public `types` contract.
 *
 * See `docs/superpowers/specs/2026-06-01-sse-base-layer-design.md`.
 */

import { useApiStore } from 'src/core/stores/api';
import { useUserStore } from 'src/core/stores/user';
import {
  classifyError,
  classifyResponse,
  type ResponseAction,
} from 'src/core/services/eventStream/classify';
import { SSEParser } from 'src/core/services/eventStream/parser';
import type {
  EventStreamCloseReason,
  EventStreamConnection,
  EventStreamOptions,
  EventStreamStatus,
  RawEventStreamMessage,
} from 'src/core/services/eventStream/types';

/** Default reconnect/backoff and timeout tuning. */
const DEFAULT_BASE_DELAY_MS = 1000;
const DEFAULT_MAX_DELAY_MS = 30000;
const DEFAULT_MAX_RETRIES = Infinity;
const DEFAULT_CONNECT_MS = 20000;
const DEFAULT_STALE_MS = 0;

/**
 * How long a tab may stay hidden before its stream is paused. Quick tab
 * switches keep the connection; a forgotten tab stops holding one of the
 * browser's ~6 HTTP/1.1 connections per host, which every API call shares.
 */
export const HIDDEN_GRACE_MS = 15000;

/** Whether a thrown value is an `AbortError` (our timeout/lifecycle aborts). */
function isAbortError(err: unknown): boolean {
  return (err as { name?: string } | null | undefined)?.name === 'AbortError';
}

/**
 * Open and run a typed event stream. Connection starts immediately; the
 * returned handle exposes live status and an idempotent `close()`.
 *
 * @typeParam TEvent - The feature's typed event, produced by `parse`.
 */
export function createEventStream<TEvent>(
  options: EventStreamOptions<TEvent>
): EventStreamConnection {
  const {
    url,
    method = 'GET',
    body,
    onMessage,
    onOpen,
    onReconnect,
    onError,
    onClose,
    onStatusChange,
    headers: staticHeaders,
    lastEventIdParam,
  } = options;

  const parse =
    options.parse ??
    ((raw: RawEventStreamMessage) => JSON.parse(raw.data) as TEvent);

  const baseDelayMs = options.reconnect?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
  const maxDelayMs = options.reconnect?.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;
  const maxRetries = options.reconnect?.maxRetries ?? DEFAULT_MAX_RETRIES;
  const connectMs = options.timeouts?.connectMs ?? DEFAULT_CONNECT_MS;
  const staleMs = options.timeouts?.staleMs ?? DEFAULT_STALE_MS;

  // --- Explicit, minimal state -------------------------------------------
  let status: EventStreamStatus = 'connecting';
  let lastEventId: string | null = options.lastEventId ?? null;
  let attempt = 0; // consecutive failed reconnects
  let closed = false; // terminal guard
  let refreshedThisCycle = false; // reset on every successful open
  let pausing = false; // the current attempt is being aborted because the tab is hidden
  let inFlight = false; // an attempt is connecting or streaming (vs backoff, token refresh)
  let hiddenAt: number | null = null; // wall-clock time the tab was hidden
  let pauseTimer: ReturnType<typeof setTimeout> | null = null;

  // Whole-connection lifecycle controller; aborted exactly once in finish().
  const lifecycle = new AbortController();
  // Per-attempt controller; replaced each loop iteration.
  let abortCurrent: AbortController = new AbortController();

  function setStatus(next: EventStreamStatus): void {
    if (status === next) {
      return;
    }
    status = next;
    onStatusChange?.(next);
  }

  // --- URL + headers ------------------------------------------------------

  /** Resolve the API base URL the same way the shared axios instance does. */
  function apiBase(): string {
    return (
      useApiStore().service.defaults.baseURL ||
      import.meta.env.VITE_API_URL ||
      ''
    );
  }

  /** Build the request URL, recomputed every (re)connect for the resume param. */
  function buildUrl(): string {
    const isAbsolute = /^https?:\/\//i.test(url);
    const resolved = isAbsolute ? url : apiBase() + url;

    if (lastEventIdParam == null || lastEventId == null) {
      return resolved;
    }

    const u = new URL(resolved, window.location.origin);
    u.searchParams.set(lastEventIdParam, lastEventId);
    return u.toString();
  }

  /**
   * Build request headers. Managed headers (`Content-Type`, `Accept`,
   * `Authorization`, `Last-Event-ID`) are spread last so they always win over
   * caller `headers`. `Content-Type: application/json` is set only when a JSON
   * body is sent (non-GET request with a body).
   */
  function buildHeaders(
    token: string,
    jsonBody: boolean
  ): Record<string, string> {
    return {
      ...staticHeaders,
      ...(jsonBody ? { 'Content-Type': 'application/json' } : {}),
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(lastEventId != null ? { 'Last-Event-ID': lastEventId } : {}),
    };
  }

  // --- Auth ---------------------------------------------------------------

  /** Refresh the access token once. Returns whether a new token was obtained. */
  async function refresh(): Promise<boolean> {
    const us = useUserStore();
    if (!us.refresh_token) {
      return false;
    }
    const data = await us.refreshToken({ refresh_token: us.refresh_token });
    return Boolean(data?.access_token);
  }

  // --- Timing helpers -----------------------------------------------------

  /** Full-jitter exponential backoff for attempt `n` (1-based). */
  function backoff(n: number): number {
    return Math.random() * Math.min(maxDelayMs, baseDelayMs * 2 ** (n - 1));
  }

  /** A timed delay that also resolves early if the lifecycle aborts. */
  function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const onAbort = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(() => {
        lifecycle.signal.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      if (lifecycle.signal.aborted) {
        onAbort();
        return;
      }
      lifecycle.signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  /** A connect can succeed now: the tab is visible and the browser is not offline. */
  function canConnect(): boolean {
    if (typeof document === 'undefined') {
      return true;
    }
    // `onLine === false` is reliable; `true` can be optimistic, which only
    // means today's retry behaviour.
    return !document.hidden && navigator.onLine !== false;
  }

  /**
   * Resolve once a connect can succeed (see `canConnect`), or early if the
   * lifecycle aborts. Waiting while offline keeps a retry from failing at once
   * and spending the retry budget.
   */
  function waitUntilConnectable(): Promise<void> {
    if (canConnect()) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const cleanup = () => {
        document.removeEventListener('visibilitychange', onChange);
        window.removeEventListener('online', onChange);
        lifecycle.signal.removeEventListener('abort', onAbort);
      };
      const onChange = () => {
        if (canConnect()) {
          cleanup();
          resolve();
        }
      };
      const onAbort = () => {
        cleanup();
        resolve();
      };
      document.addEventListener('visibilitychange', onChange);
      window.addEventListener('online', onChange);
      lifecycle.signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  // --- Pause while hidden ---------------------------------------------------

  /**
   * Abort the current attempt so the run loop parks until the tab is visible.
   * Only once a resume id exists: without one a reconnect cannot continue where
   * this stream stopped, and the server's work may not have started yet.
   */
  function pauseIfResumable(): void {
    if (closed || lastEventId === null) {
      return;
    }
    pausing = true;
    if (inFlight) {
      abortCurrent.abort(); // the run loop turns the abort into 'paused'
    } else {
      // Between attempts (retry backoff, token refresh) there is nothing to
      // abort; the loop top already holds the next connect until visible.
      setStatus('paused');
    }
  }

  /** Hidden for the grace period by the wall clock (timers stall in a sleeping or frozen tab). */
  function hiddenPastGrace(): boolean {
    return hiddenAt !== null && Date.now() - hiddenAt >= HIDDEN_GRACE_MS;
  }

  function clearPauseTimer(): void {
    if (pauseTimer !== null) {
      clearTimeout(pauseTimer);
      pauseTimer = null;
    }
  }

  /** Only signals: the run loop stays the sole place that opens connections. */
  function onVisibilityChange(): void {
    if (document.hidden) {
      hiddenAt = Date.now();
      clearPauseTimer();
      pauseTimer = setTimeout(() => {
        pauseTimer = null;
        pauseIfResumable();
      }, HIDDEN_GRACE_MS);
      return;
    }
    const overdue = hiddenPastGrace();
    hiddenAt = null;
    clearPauseTimer();
    // The grace timer never ran (sleep, frozen tab), so an "open" stream may
    // have died unseen: reconnect now rather than trust it.
    if (overdue && status === 'open') {
      pauseIfResumable();
    } else if (status === 'paused') {
      // Paused between attempts: the next connect is due now, or when the
      // backoff ends.
      setStatus('reconnecting');
    }
  }

  // --- Stream consumption -------------------------------------------------

  /** Commit a frame's resume id, then hand the parsed event to the consumer. */
  function dispatch(frame: RawEventStreamMessage): void {
    if (frame.id !== null) {
      lastEventId = frame.id;
    }
    try {
      onMessage(parse(frame));
    } catch (err) {
      // A parse/handler throw must NOT drop the connection.
      onError?.(err);
    }
    // Hidden past the grace period with no pause yet: the first resume id just
    // arrived, or the grace timer stalled.
    if (!pausing && hiddenPastGrace()) {
      pauseIfResumable();
    }
  }

  /**
   * Drain the response body until clean EOF (returns) or an abort/network
   * failure (throws). Resets the optional stale timer on every chunk of bytes.
   */
  async function consumeStream(
    body: NonNullable<Response['body']>
  ): Promise<void> {
    const reader = body.getReader();
    const parser = new SSEParser();
    let staleTimer: ReturnType<typeof setTimeout> | null = null;

    const armStale = () => {
      if (staleMs <= 0) {
        return;
      }
      if (staleTimer !== null) {
        clearTimeout(staleTimer);
      }
      staleTimer = setTimeout(() => abortCurrent.abort(), staleMs);
    };

    try {
      armStale();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) {
          for (const frame of parser.end()) {
            dispatch(frame);
          }
          break;
        }
        armStale();
        for (const frame of parser.feed(value as Uint8Array)) {
          dispatch(frame);
        }
      }
    } finally {
      if (staleTimer !== null) {
        clearTimeout(staleTimer);
      }
      reader.releaseLock?.();
    }
  }

  /**
   * Perform one connect attempt and, if it opens a stream, consume it to EOF.
   * Returns the action the run loop should take next; throws on abort/network.
   */
  async function openAndConsume(): Promise<ResponseAction> {
    setStatus(status === 'connecting' ? 'connecting' : 'reconnecting');

    const token = useUserStore().access_token;

    // A request body only rides on non-GET requests (fetch forbids a GET body).
    // Serialised on every (re)connect; a function body is re-evaluated each time.
    const payload = typeof body === 'function' ? body() : body;
    const hasBody = method !== 'GET' && payload != null;

    const connectTimer =
      connectMs > 0 ? setTimeout(() => abortCurrent.abort(), connectMs) : null;

    let response: Response;
    try {
      response = (await fetch(buildUrl(), {
        method,
        headers: buildHeaders(token, hasBody),
        ...(hasBody ? { body: JSON.stringify(payload) } : {}),
        signal: abortCurrent.signal,
      })) as Response;
    } finally {
      if (connectTimer !== null) {
        clearTimeout(connectTimer);
      }
    }

    const action = classifyResponse(response);
    if (action.kind !== 'stream') {
      // complete / refresh / fatal / retry are handled by run().
      return action;
    }

    // Healthy open: reset backoff and the one-shot refresh cycle.
    attempt = 0;
    refreshedThisCycle = false;
    setStatus('open');
    onOpen?.();

    await consumeStream(response.body as NonNullable<Response['body']>);

    // Clean EOF -> reconnect (a 204 on the next connect will end it).
    return { kind: 'retry' };
  }

  // --- Teardown -----------------------------------------------------------

  /** Reach the terminal `closed` state exactly once. */
  function finish(reason: EventStreamCloseReason): void {
    if (closed) {
      return;
    }
    closed = true;
    clearPauseTimer();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }
    setStatus('closed');
    abortCurrent?.abort();
    lifecycle.abort();
    onClose?.(reason);
  }

  // --- The run loop -------------------------------------------------------

  async function run(): Promise<void> {
    while (!closed) {
      // Never connect from a hidden tab or an offline browser: covers a tab
      // opened in the background, a resume after a pause, and a reconnect that
      // came due while hidden or offline.
      await waitUntilConnectable();
      if (closed) {
        break;
      }
      pausing = false;
      abortCurrent = new AbortController();

      // Relay lifecycle aborts onto the current attempt.
      const relay = () => abortCurrent.abort();
      if (lifecycle.signal.aborted) {
        relay();
      } else {
        lifecycle.signal.addEventListener('abort', relay, { once: true });
      }

      let action: ResponseAction;
      inFlight = true;
      try {
        action = await openAndConsume();
      } catch (err) {
        if (closed) {
          break;
        }
        // Our own timeout/lifecycle/pause aborts are transient -> retry.
        action = isAbortError(err) ? { kind: 'retry' } : classifyError(err);
      } finally {
        inFlight = false;
        lifecycle.signal.removeEventListener('abort', relay);
      }

      if (closed) {
        break;
      }

      if (pausing && action.kind === 'retry') {
        // Hidden, not failed, even when a server EOF or a dying socket raced
        // our abort: no backoff, no retry budget, no onReconnect. The loop top
        // resumes with Last-Event-ID once the tab is visible.
        setStatus('paused');
        continue;
      }

      switch (action.kind) {
        case 'complete':
          finish('completed');
          return;

        case 'fatal':
          finish('forbidden');
          return;

        case 'refresh': {
          if (refreshedThisCycle) {
            finish('unauthorized');
            return;
          }
          refreshedThisCycle = true;
          if (!(await refresh())) {
            finish('unauthorized');
            return;
          }
          // Immediate retry: no backoff, do not increment attempt.
          continue;
        }

        default: {
          // 'retry' — also a clean server EOF.
          attempt += 1;
          if (attempt > maxRetries) {
            finish('retries-exhausted');
            return;
          }
          setStatus('reconnecting');
          onReconnect?.(lastEventId, attempt);
          // A hidden or offline tab still waits out the backoff; the loop top
          // then holds the connect until it can succeed.
          await delay(action.retryAfterMs ?? backoff(attempt));
          if (closed) {
            return;
          }
        }
      }
    }
  }

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
  void run();

  return {
    get status() {
      return status;
    },
    get lastEventId() {
      return lastEventId;
    },
    close() {
      finish('manual');
    },
  };
}
